import { apiCall } from "./firebase";

export interface PluginConnectionStatus {
  connected: boolean;
  hasValidTokens: boolean;
  lastChecked: Date;
  error?: string;
  requiresReauth?: boolean;
}

export interface PluginConnectionResponse {
  success: boolean;
  userId: string;
  pluginType: string;
  connectionStatus: PluginConnectionStatus;
}

class OAuthHealthService {
  private cache: Map<string, { data: PluginConnectionStatus; timestamp: number }> = new Map();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  /**
   * Check OAuth health for a specific plugin
   */
  async checkPluginConnection(pluginType: string, forceRefresh = false): Promise<PluginConnectionStatus> {
    const now = Date.now();
    const cacheKey = pluginType;

    console.log(`🔍 OAuthHealthService: Checking ${pluginType} connection (forceRefresh: ${forceRefresh})`);

    // Return cached result if still valid and not forcing refresh
    if (!forceRefresh && this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey);
      if (cached && now - cached.timestamp < this.CACHE_DURATION) {
        console.log(`🔍 OAuthHealthService: Using cached result for ${pluginType}:`, cached.data);
        return cached.data;
      }
    }

    try {
      console.log(`🔍 OAuthHealthService: Making API call for ${pluginType}`);
      const response = await apiCall(`/getPluginConnectionStatus?pluginType=${encodeURIComponent(pluginType)}`);

      console.log(`🔍 OAuthHealthService: API response for ${pluginType}:`, response);

      if (!response.success) {
        throw new Error(response.error || "Failed to check plugin connection");
      }

      // Convert date string back to Date object
      const connectionStatus: PluginConnectionStatus = {
        ...response.connectionStatus,
        lastChecked: new Date(response.connectionStatus.lastChecked),
      };

      console.log(`🔍 OAuthHealthService: Processed connection status for ${pluginType}:`, connectionStatus);

      // Cache the result
      this.cache.set(cacheKey, { data: connectionStatus, timestamp: now });

      return connectionStatus;
    } catch (error) {
      console.error(`OAuthHealthService: Failed to check ${pluginType} connection:`, error);
      throw error;
    }
  }

  /**
   * Check if a specific plugin is healthy (connected, has valid tokens, and doesn't require reauth)
   */
  async isPluginHealthy(pluginType: string): Promise<boolean> {
    try {
      console.log(`🔍 OAuthHealthService: Checking if ${pluginType} is healthy`);
      const status = await this.checkPluginConnection(pluginType);

      const isHealthy = status.connected && status.hasValidTokens && !status.requiresReauth;

      console.log(`🔍 OAuthHealthService: ${pluginType} health check result:`, {
        connected: status.connected,
        hasValidTokens: status.hasValidTokens,
        requiresReauth: status.requiresReauth,
        isHealthy,
        error: status.error,
      });

      return isHealthy;
    } catch (error) {
      console.error(`OAuthHealthService: Failed to check ${pluginType} health:`, error);
      return false;
    }
  }

  /**
   * Clear cached data
   */
  clearCache(): void {
    this.cache.clear();
  }
}

// Create singleton instance
export const oauthHealthService = new OAuthHealthService();
